import { useRef } from 'react';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { Button } from '../ui/Button';
import { STICKY_FOOTER_HEIGHT_VAR } from '../ui/toast';
import { useFooterSaveState, type FooterSaveStatus } from './footerSave';

const STATUS_TEXT: Record<FooterSaveStatus, string> = {
  clean: 'No unsaved changes',
  dirty: 'Unsaved changes on this page',
  saved: 'All changes saved',
};

/**
 * The page's one Save, in a bar pinned to the bottom of the content area (spec "Saving and unsaved
 * changes"): the status on the left, Save on the right. Save is disabled until a card has a change it
 * can save, and saves every changed card at once (FooterSaveState.saveAll). Each card keeps its own
 * error; the "Changes saved" toast confirms a save that worked.
 *
 * Renders inside a FooterSaveProvider (nothing outside one), as the LAST child of a block that spans
 * the page's content: `sticky` holds it to that block's bottom while the page scrolls. On a settings
 * page that block (SettingsTabs') fills the content area's height, so with less than a window of
 * content the footer still rests at the bottom of the content area; elsewhere it rests at the end of
 * the content.
 *
 * Laid out for StandardLayout's scroll region (`py-6`, then a `px-6` column):
 * - `-bottom-6`: a sticky box stops at its scroll container's PADDING edge (measured in Chrome, see
 *   ComposerModal), so a plain `bottom-0` would float 1.5rem above the window's edge with the page
 *   scrolling visibly beneath it. Minus the padding, it sits on the edge.
 * - `-mb-6`: eats that same bottom padding where the page ends, so the bar never jumps up 1.5rem as the
 *   last of the page scrolls into view.
 * - `-mx-6 px-6`: runs the bar across the column's side padding, the full width of the content area.
 * - `z-30`: over the cards scrolling under it, under a card's open menu (z-40), the toast (z-[48]) and
 *   modals (z-50).
 *
 * While it is on screen it keeps its height in `--sticky-save-footer-h` on <html>, so the toast sits
 * above it, and SettingsTabs' tab list stops short of it.
 */
export function StickySaveFooter() {
  const state = useFooterSaveState();
  const shown = state !== null;
  const bar = useRef<HTMLDivElement>(null);

  useIsoLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const root = document.documentElement;
    const write = () => root.style.setProperty(STICKY_FOOTER_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
    write();
    // Written in the next frame, never inside the observer's callback, as everywhere else here.
    let frame: number | null = null;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (frame !== null) return;
            frame = requestAnimationFrame(() => {
              frame = null;
              write();
            });
          });
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      root.style.removeProperty(STICKY_FOOTER_HEIGHT_VAR);
    };
  }, [shown]);

  if (!state) return null;
  return (
    <div
      ref={bar}
      data-save-footer=""
      className="sticky -bottom-6 z-30 -mx-6 -mb-6 mt-6 border-t border-line bg-card/95 px-6 py-3 backdrop-blur"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          {state.status === 'dirty' && (
            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-amber-500 dark:bg-warning-dark" />
          )}
          {STATUS_TEXT[state.status]}
        </p>
        <Button onClick={() => void state.saveAll()} loading={state.saving} disabled={!state.canSave}>
          Save
        </Button>
      </div>
    </div>
  );
}
